import { Component, Input, Output, EventEmitter, OnInit, SecurityContext, OnDestroy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UntypedFormControl, Validators } from '@angular/forms';

import { forkJoin, of, Subscription as RxSubscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BalanceInfo } from 'voxloud-types/dist/models';

import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { UtilsService } from 'src/app/shared/services/utils/utils.service';
import { SubscriptionService } from 'src/app/domains/subscription/services/';
import { CompanyService } from 'src/app/domains/companies/services';
import { BillingInfo,
  CallRecordingConfiguration,
  CreditModel,
  PaymentInfoModel,
  PBX,
  PivotConfig,
  PivotModel,
  Subscription
 } from 'src/app/domains/subscription/models';
import {
  AdditionalCompanyData,
  BusinessCategoryType,
  Company,
  CompanyChatInfo,
  CONTROL_PANEL_ENVIRONMENT,
  NOTE_TYPES
} from 'src/app/domains/companies/models';
import { PartnerService } from 'src/app/domains/partner/services/partner.service';
import { CompanyPartner } from 'src/app/domains/partner/models/partner.model';

import { EditNotesPopupComponent } from '../edit-notes-popup/edit-notes-popup.component';
import { WipLoginPopupComponent } from '../wip-login-popup/wip-login-popup.component';
import { ConnectivityService } from 'src/app/domains/connectivity/services/connectivity.service';
import { ConnectivityItem } from 'src/app/domains/connectivity/models/connectivity.model';
import { PbxService } from 'src/app/domains/companies/services/pbx.service';
import { GenerateUpdatePaymentLinkPopupComponent } from '../generate-update-payment-link-popup/generate-update-payment-link-popup.component';

@Component({
  selector: 'gdp-company-overview',
  templateUrl: './company-overview.component.html',
  styleUrls: ['./company-overview.component.scss']
})
export class CompanyOverviewComponent implements OnInit, OnDestroy {
  @Input() company: Company;
  @Output('companyChanged') companyChanged = new EventEmitter<Company>();
  editing = false;
  setHubspot = false;
  loadingData = {
    subscription: false,
    credit: false,
    pivot: false,
    documents: false,
    recordings: {
      outbound_enabled: false,
      inbound_enabled: false,
      internal_enabled: false
    },
    company: false,
    kazooInfo: false,
    readOnlyMode: false,
    partner: false,
    addtionalData: false,
    kazooLink: false,
    paymentInfo: false,
    channelInfo: {
      general: false,
      internal: false
    },
    kazooLock: false,
    antifraudLock: false,
    updateHubspot: false,
    chatInfo: false,
  };

  subscription: Subscription;
  fraudLocked: boolean;
  hasPivot: boolean;
  channelGeneralNotificationsEnabled: boolean;
  channelLimitSlackInternalMessageEnabled: boolean;
  callLimit: number | string;
  ENV = CONTROL_PANEL_ENVIRONMENT;
  creditInput = new UntypedFormControl(0);
  newNameInput = new UntypedFormControl('', Validators.required);
  dealIdInput = new UntypedFormControl('', Validators.required);
  kazooInstance: string;
  kazooLocked: boolean = false;
  linkToKazoo: string;
  paymentInfo: any;
  locale: string;
  partnerData: CompanyPartner;
  additionalCompanyData: AdditionalCompanyData;
  NOTE_TYPES = NOTE_TYPES;
  canWipLogin = !environment.production;
  connections: ConnectivityItem[] = [];
  billingInfo: BillingInfo;
  callRecordingConfiguration: CallRecordingConfiguration;
  balanceInfo: BalanceInfo;
  showVipLabel: boolean = false;
  public businessCategoryAndType: {
    category: BusinessCategoryType;
    type: BusinessCategoryType | string;
  };
  public isPivotDisabled: boolean = false;
  public companyChatInfo: CompanyChatInfo | null = null;

  private language: string;
  private subscriptions: RxSubscription[] = [];

  constructor(
    private companyService: CompanyService,
    private pbxService: PbxService,
    private subscriptionService: SubscriptionService,
    private partnerService: PartnerService,
    private notification: NotificationService,
    private router: Router,
    private translate: TranslateService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private datePipe: DatePipe,
    private connectivityService: ConnectivityService,
    private notificationService: NotificationService,
    private utils: UtilsService
  ) { }

  ngOnInit() {
    this.language = this.translate.currentLang;
    this.subscriptions.push(
      this.translate.onLangChange.subscribe((l: LangChangeEvent) => {
        this.language = l.lang;
        this.getBusinessCategoryAndTypeByLang();
      })
    )
    this.loadingData = {
      ...this.loadingData,
      subscription: true,
      credit: true,
      pivot: true,
      kazooInfo: true,
      addtionalData: true,
      partner: true,
      kazooLink: true,
      paymentInfo: true,
      channelInfo: {
        general: true,
        internal: true
      },
      kazooLock: true,
      antifraudLock: true,
      readOnlyMode: true,
      chatInfo: true,
      recordings: {
        outbound_enabled: true,
        inbound_enabled: true,
        internal_enabled: true
      }
    };
    this.company.locking = true;
    this.subscriptions.push(
      forkJoin({
        pbxes: this.pbxService.getPbxes(this.company.pbxId).pipe(catchError(() => { return of(undefined) })),
        kazooLink: this.pbxService.getLinkToKazoo(this.company.pbxId).pipe(catchError(() => { return of(undefined) })),
        paymentInfo: this.subscriptionService.getPaymentInfo(this.company.companyId).pipe(catchError(() => { return of(undefined) })),
        subscription: this.subscriptionService.getSubscription(this.company.companyId).pipe(catchError(() => { return of(undefined) })),
        credit: this.companyService.getCredit(this.company.companyId).pipe(catchError(() => { return of(undefined) })),
        pivot: this.pbxService.getPivotStatus(this.company.pbxId).pipe(catchError(() => { return of(undefined) })),
        additionalCompanyData: this.companyService.getCustomerCompany(this.company.companyId).pipe(catchError(() => { return of(undefined) })),
        partner: this.partnerService.getCompanyPartner(this.company.companyId).pipe(catchError(() => { return of(undefined) })),
        channelInfo: this.pbxService.getPivotConfig(this.company.pbxId).pipe(catchError(() => { this.isPivotDisabled = true; return of(undefined) })),
        billingInfo: this.subscriptionService.getBillingInfo(this.company.companyId).pipe(catchError(() => { return of(undefined) })),
        balanceInfo: this.subscriptionService.getBalanceInfo(this.company.billingId).pipe(catchError(() => { return of(undefined) })),
        chatInfo: this.companyService.getCompanyChatInfo(this.company.companyId).pipe(catchError(() => of(null)))
      })
        .subscribe((res: {
          pbxes: PBX,
          kazooLink: string,
          paymentInfo: PaymentInfoModel,
          subscription: Subscription,
          credit: CreditModel,
          pivot: PivotModel,
          additionalCompanyData: AdditionalCompanyData,
          partner: CompanyPartner,
          channelInfo: PivotConfig,
          connectivity: ConnectivityItem[],
          billingInfo: BillingInfo,
          balanceInfo: BalanceInfo[],
          chatInfo: CompanyChatInfo | null
        }) => {
          this.kazooInstance = res.pbxes ? res.pbxes?.kazoo_instance_id : '-';
          this.kazooLocked = res.pbxes.hasOwnProperty('kazoo_account_enabled') ? !res.pbxes?.kazoo_account_enabled : false;
          this.linkToKazoo = res.kazooLink;
          this.paymentInfo = res.paymentInfo;
          this.subscription = res.subscription;
          this.fraudLocked = res.subscription?.fraud_prevention_enabled;
          this.company.balance = res.credit != undefined ? res.credit?.value : undefined;
          this.hasPivot = res.pivot ? res.pivot?.inbound === 'ENABLED' && res.pivot?.outbound === 'ENABLED' : undefined;
          this.additionalCompanyData = res.additionalCompanyData;
          this.getBusinessCategoryAndTypeByLang();
          this.partnerData = res.partner;
          this.channelGeneralNotificationsEnabled = res.channelInfo?.channelLimitNotificationsEnabled;
          this.channelLimitSlackInternalMessageEnabled = res.channelInfo?.channelLimitSlackInternalMessageEnabled;
          this.callLimit = res.channelInfo ? res.channelInfo?.callLimit : '-';
          this.connections = res.connectivity;
          this.billingInfo = res.billingInfo;
          this.callRecordingConfiguration = res.pbxes.call_recording_configuration;
          this.balanceInfo = res.balanceInfo[0];
          this.companyChatInfo = res.chatInfo;
          this.showVipLabel = res.subscription?.addons.find(addon => addon.id.includes('vip_support')) !== undefined;
          this.loadingData = {
            ...this.loadingData,
            subscription: false,
            credit: false,
            pivot: false,
            kazooInfo: false,
            addtionalData: false,
            partner: false,
            kazooLink: false,
            paymentInfo: false,
            channelInfo: {
              general: false,
              internal: false
            },
            kazooLock: false,
            antifraudLock: false,
            readOnlyMode: false,
            chatInfo: false,
            recordings: {
              outbound_enabled: false,
              inbound_enabled: false,
              internal_enabled: false
            }
          };
          this.company.locking = false;
        }, () => {
        })
    );
    this.locale = `${this.translate.currentLang}-${this.translate.currentLang.toUpperCase()}`;
    this.subscriptions.push(
      this.translate.onLangChange.subscribe(l => {
        this.locale = `${l.lang}-${l.lang.toUpperCase()}`;
      })
    );
    this.newNameInput.setValue(this.company.name);
  }

  saveName() {
    this.loadingData.company = true;
    this.subscriptions.push(
      this.companyService.updateCompany({ ...this.company, name: this.newNameInput.value })
        .subscribe(
          () => {
            this.editing = false;
            this.loadingData.company = false;
            this.company.name = this.newNameInput.value;
            this.companyChanged.emit(this.company);
          },
          error => {
            this.editing = false;
            this.loadingData.company = false;
          }
        )
    );
    this.editing = false;
  }

  setDealId() {
    this.loadingData.updateHubspot = true;
    this.subscriptions.push(
      this.companyService.updateCustomerSupportCompany({
        company_id: this.company.companyId,
        company_name: this.company.name,
        company_language: this.additionalCompanyData.language,
        billing_country: this.additionalCompanyData.billing_country,
        hubspot_deal_id: this.dealIdInput.value,
        notes_administration: this.additionalCompanyData.notes_administration,
        notes_sales: this.additionalCompanyData.notes_sales,
        notes_success: this.additionalCompanyData.notes_success,
        notes_support: this.additionalCompanyData.notes_support
      })
        .subscribe(
          (additionalData) => {
            this.setHubspot = false;
            this.additionalCompanyData.hubspot_data = additionalData.hubspot_data;
            this.loadingData.updateHubspot = false;
          },
          error => {
            this.setHubspot = false;
            this.loadingData.updateHubspot = false;
          }
        )
    );
    this.setHubspot = false;
    this.dealIdInput.reset('');
  }

  openControlPanel(
    company: Company,
    env: CONTROL_PANEL_ENVIRONMENT
  ): void {
    this.companyService.openControlPanel(company, env);
  }

  toggleReadOnly(evt: boolean): void {
    const message = this.company.readOnly
    ? 'companies.confirm-disable-read-only'
    : 'companies.confirm-enable-read-only';
    const newValue = evt;
    this.company.readOnly = newValue;
    const newReadOnlyCompany = {
      ...this.company,
      readOnly: newValue
    }

    this.notification.showConfirmationWithAlphaname(
      message,
      this.company.alphaname).then((result: string) => {
      if (result === 'ok') {
        this.loadingData.readOnlyMode = true;
        this.companyService.toggleReadOnlyMode(newReadOnlyCompany)
          .subscribe(
            (res: any) => {
              this.company.readOnly = res.readOnly;
              this.loadingData.readOnlyMode = false;
            }, () => {
              this.company.readOnly = !newValue;
              this.loadingData.readOnlyMode = false;
            });
      } else {
        this.company.readOnly = !newValue;
      }
    });
  }

  toggleRecordings(name: string, evt: boolean): void {
    this.loadingData.recordings[name] = true;
    this.callRecordingConfiguration[name] = evt;
    const newCallRecordingConfiguration = {
      ...this.callRecordingConfiguration,
      [name]: evt
    };

    this.subscriptions.push(
      this.pbxService.updateCallRecordingConfiguration(this.company.pbxId, newCallRecordingConfiguration).subscribe((resp: CallRecordingConfiguration) => {
        this.callRecordingConfiguration = resp;
        this.loadingData.recordings[name] = false;
      }, () => {
        this.callRecordingConfiguration[name] = !newCallRecordingConfiguration[name];
        this.loadingData.recordings[name] = false;
      })
    );
  }

  togglePivot() {
    this.loadingData.pivot = true;
    const data = this.hasPivot
      ? { inbound: "DISABLE", outbound: "DISABLE" }
      : { inbound: "ENABLE", outbound: "ENABLE" };
    const previousState = this.hasPivot;
    this.hasPivot = !this.hasPivot;

    this.subscriptions.push(
      this.pbxService.setPivot(this.company.pbxId, data).subscribe(
        () => {
          this.loadingData.pivot = false;
        }, () => {
          this.hasPivot = previousState;
          this.loadingData.pivot = false;
        }
      )
    );
  }

  toggleChannelLimitGeneralNotifications(evt: boolean) {
    this.loadingData.channelInfo.general = true;
    const previousState = this.channelGeneralNotificationsEnabled;
    this.channelGeneralNotificationsEnabled = evt;

    this.subscriptions.push(
      this.pbxService.toggleChannelLimit(this.company.pbxId).subscribe(
        (res: PivotConfig) => {
          this.channelGeneralNotificationsEnabled = res.channelLimitNotificationsEnabled;
          this.loadingData.channelInfo.general = false;
        }, () => {
          this.channelGeneralNotificationsEnabled = previousState;
          this.loadingData.channelInfo.general = false;
        }
      )
    );
  }

  toggleChannelLimitInternalNotifications(evt: boolean) {
    this.loadingData.channelInfo.internal = true;
    const previousState = this.channelLimitSlackInternalMessageEnabled;
    this.channelLimitSlackInternalMessageEnabled = evt;

    this.subscriptions.push(
      this.pbxService.toggleChannelLimit(this.company.pbxId, 'slack_internal').subscribe(
        (res: PivotConfig) => {
          this.channelLimitSlackInternalMessageEnabled = res.channelLimitSlackInternalMessageEnabled;
          this.loadingData.channelInfo.internal = false;
        }, () => {
          this.channelLimitSlackInternalMessageEnabled = previousState;
          this.loadingData.channelInfo.internal = false;
        }
      )
    );
  }

  addCredit() {
    this.loadingData.credit = true;
    if (this.creditInput.value) {
      this.subscriptions.push(
        this.companyService.addCredit(this.company.companyId, this.creditInput.value)
          .subscribe(
            () => {
              this.loadingData.credit = false;
              this.company.balance = this.company.balance + this.creditInput.value;
              this.creditInput.setValue(0);
            }, () => {
              this.loadingData.credit = false;
              this.creditInput.setValue(0);
            }
          )
      );
    } else {
      this.creditInput.setValue(0);
      this.notification.error('company.overview.credit-error-empty');
      this.loadingData.credit = false;
    }
  }

  togglePaymentVerification() {
    this.loadingData.subscription = true;
    const oldValue = this.subscription.payment_verification_required;
    this.subscription.payment_verification_required = !this.subscription.payment_verification_required;
    this.subscriptions.push(
      this.subscriptionService.setPaymentVerification(
        this.company,
        this.subscription.payment_verification_required
      ).subscribe(
        r => {
          this.loadingData.subscription = false;
          this.subscription.payment_verification_required = this.subscription.payment_verification_required;
        }, () => {
          this.subscription.payment_verification_required = oldValue;
          this.loadingData.subscription = false;
        }
      )
    );
  }

  deleteCompany(c: Company) {
    this.notification.showConfirmation('companies.confirm-delete').then((result: string) => {
      if (result === 'ok') {
        c.deleting = true;
        this.subscriptions.push(
          this.companyService.deleteCompany(c).subscribe(r => {
            delete c.deleting;
            this.router.navigate(['/dashboard/companies']);
          }, () => {
            delete c.deleting;
          })
        );
      }
    });
  }

  toggleAntifraudLock(evt: boolean) {
    const message = this.fraudLocked
      ? 'companies.confirm-anti-fraud-unlock'
      : 'companies.confirm-anti-fraud-lock';
    const newValue = evt;
    this.fraudLocked = newValue;

    this.notificationService.showConfirmationWithAlphaname(
      message,
      this.company.alphaname).then((result: string) => {
        if (result === 'ok') {
          this.loadingData.antifraudLock = true;
          this.subscriptions.push(
            this.subscriptionService.updateAntiFraudLock(this.company.companyId, evt).subscribe(() => {
              this.fraudLocked = newValue;
              this.loadingData.antifraudLock = false;
            }, () => {
              this.fraudLocked = !newValue;
              this.loadingData.antifraudLock = false;
            })
          );
        } else {
          this.fraudLocked = !newValue;
          this.loadingData.antifraudLock = false;
        }
      });
  }

  toggleLock(): void {
    this.company.locking = true;
    this.company.locked
      ? this.unlockCompany(this.company)
      : this.lockCompany(this.company);
  }

  private unlockCompany(c: Company): void {
    const message = this.lockUnlockConnections()
      ? 'companies.confirm-unlock-connectivity'
      : 'companies.confirm-unlock';
    c.locked = false;

    this.notification.showConfirmationWithAlphaname(
      message,
      c.alphaname
      ).then((result: string) => {
      if (result === 'ok') {
        c.locking = true;
        if (this.lockUnlockConnections()) {
          this.subscriptions.push(
            forkJoin({
              unlockCompany: this.companyService.unlockCompany(c),
              unlockConnectivity: this.connectivityService.unlockConnectivity(c.companyId).pipe(catchError(() => { return of(undefined) }))
            }).subscribe((res) => {
              c.locked = false;
              this.connections = res.unlockConnectivity;
              delete c.locking;
            }, () => {
              c.locked = true;
              delete c.locking;
            })
          );
        } else {
          this.subscriptions.push(
            this.companyService.unlockCompany(c).subscribe(r => {
              c.locked = false;
              delete c.locking;
            }, () => {
              c.locked = true;
              delete c.locking;
            })
          );
        }
      } else {
        c.locked = true;
        delete c.locking;
      }
    });
  }

  private lockCompany(c: Company): void {
    const message = this.lockUnlockConnections()
      ? 'companies.confirm-lock-connectivity'
      : 'companies.confirm-lock';
    c.locked = true;

    this.notification.showConfirmationWithAlphaname(
      message,
      c.alphaname).then((result: string) => {
      if (result === 'ok') {
        c.locking = true;
        if (this.lockUnlockConnections()) {
          this.subscriptions.push(
            forkJoin({
              lockCompany: this.companyService.lockCompany(c),
              lockConnectivity: this.connectivityService.lockConnectivity(c.companyId).pipe(catchError(() => { return of(undefined) }))
            }).subscribe((res) => {
              c.locked = true;
              this.connections = res.lockConnectivity;
              delete c.locking;
            }, () => {
              c.locked = false;
              delete c.locking;
            })
          );
        } else {
          this.subscriptions.push(
            this.companyService.lockCompany(c).subscribe(r => {
              c.locked = true;
              delete c.locking;
            }, () => {
              c.locked = false;
              delete c.locking;
            })
          );
        }
      } else {
        c.locked = false;
        delete c.locking;
      }
    });
  }

  private lockUnlockConnections(): boolean {
    return this.connections?.length !== 0
    && (this.connections?.some(el => el.is_locked) === this.company.locked);
  }

  openNotesPopup(action: string, type: NOTE_TYPES): void {
    const modal = this.modalService.open(EditNotesPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.additionalCompanyData = this.additionalCompanyData;
    modal.componentInstance.action = action;
    modal.componentInstance.noteType = type;
    const emitValueSub = modal.componentInstance.emitValue.pipe(
      tap((res: string) => this.notification.success(res)),
      switchMap(() => {
        this.loadingData.addtionalData = true;
        return this.companyService.getCustomerCompany(this.company.companyId);
      }),
      finalize(() => {
        this.loadingData.addtionalData = false;
      })
    ).subscribe({
      next: (res: AdditionalCompanyData) => {
        this.additionalCompanyData = res;
        this.loadingData.addtionalData = false;
      },
      error: () => {
        this.loadingData.addtionalData = false;
      }
    });
    this.subscriptions.push(emitValueSub);
  }

  isDisabledLock(c: Company) {
    return environment.idsCompanyDisableLock.includes(c.companyId);
  }

  openWipLoginPopup() {
    const m = this.modalService.open(WipLoginPopupComponent);
    m.componentInstance.company = this.company;
  }

  onCopyChurnLink() {
    let token = {
      company_id: this.subscription.company_id,
      company_name: this.subscription.billing_info.company_name,
      administrator_name: this.subscription.billing_info.first_name,
      administrator_email: this.subscription.billing_info.invoicing_emails[0],
      plan_id: this.subscription.plan.id,
      chargebee_subscription_id: this.subscription.chargebee_subscription_id,
      subscription_activation_date: this.datePipe.transform(this.subscription.activation_date, 'medium'),
      country: this.subscriptionService.getCompanyCountry(this.subscription)
    }

    const link = this.sanitizer.sanitize(
      SecurityContext.URL,
      this.sanitizer.bypassSecurityTrustResourceUrl(
        `${environment.controlpanelUrl}/login/#/leave-us?token=${btoa(JSON.stringify(token))}`
      )
    );
    this.utils.copyToClipboard(
      link,
      'text/plain',
      () => {
        this.notification.success('company.overview.notifications.churn-link.success-copy');
      }
    );
  }

  isNotesEmpty(key: NOTE_TYPES): boolean {
    const k = `notes_${key}`;
    return !this.additionalCompanyData?.[k] || (this.additionalCompanyData?.[k] && this.additionalCompanyData?.[k].length === 0);
  }

  public lockUnlockKazoo(): void {
    this.loadingData.kazooLock = true;
    const message = this.kazooLocked
      ? 'companies.confirm-unlock-kazoo'
      : 'companies.confirm-lock-kazoo';
    const oldValue = this.kazooLocked;
    this.kazooLocked = !this.kazooLocked;
    this.notification.showConfirmationWithAlphaname(
      message,
      this.company.alphaname
    ).then((result: string) => {
      if (result === 'ok') {
        this.loadingData.kazooLock = true;
        if(oldValue) {
          this.subscriptions.push(
            this.companyService.unlockKazooAccount(this.company.pbxId).subscribe((res) => {
              this.notificationService.success('company.overview.kazoo-locked.success-unlock');
              this.kazooLocked = !oldValue;
              this.loadingData.kazooLock = false;
            })
          );
        } else {
          this.subscriptions.push(
            this.companyService.lockKazooAccount(this.company.pbxId).subscribe((res) => {
              this.notificationService.success('company.overview.kazoo-locked.success-lock');
              this.kazooLocked = !oldValue;
              this.loadingData.kazooLock = false;
            })
          );
        }
      } else {
        this.kazooLocked = !this.kazooLocked;
        this.loadingData.kazooLock = false;
      }
    });
  }

  private getBusinessCategoryAndTypeByLang(): void {
    const category: BusinessCategoryType = this.additionalCompanyData?.business_category?.business_category_translations
      .find((c: BusinessCategoryType) => c.language.toLowerCase() === this.language.toLowerCase());
    this.businessCategoryAndType = {
      category: category ? category : {id: null, name: '-'},
      type: {id: null, name: '-'}
    };
    if(!category) return;
    if(category.name.toLowerCase() !== 'others' && category.name.toLowerCase() !== 'altri') {
      const type: BusinessCategoryType = this.additionalCompanyData?.business_type?.translations
      .find((t: BusinessCategoryType) => t.language.toLowerCase() === this.language.toLowerCase());
      this.businessCategoryAndType = {
        ...this.businessCategoryAndType,
        type: type
      };
    } else {
      this.businessCategoryAndType = {
        ...this.businessCategoryAndType,
        type: {id: 0, name: this.additionalCompanyData?.other_business_type}
      };
    }
  }

  public openGenerateUpdatePaymentLinkModal(): void {
    const modal = this.modalService.open(GenerateUpdatePaymentLinkPopupComponent, {
      size: 'lg'
    });
    modal.componentInstance.companyId = this.company.companyId;
    modal.componentInstance.companyEmail = this.company.email;
    modal.componentInstance.language = this.language;
    modal.componentInstance.linkId = this.subscription?.generated_link_id;
    const emitValueSubscription = modal.componentInstance.emitValue.pipe(
      switchMap(() => {
        this.loadingData.paymentInfo = true;
        return this.subscriptionService.getSubscription(this.company.companyId);
      }),
      finalize(() => {
        this.loadingData.paymentInfo = false;
      })
    ).subscribe({
        next: (res: Subscription) => {
          this.subscription = res;
          this.loadingData.paymentInfo = false;
        },
        error: () => {
          this.loadingData.paymentInfo = false;
        }
    });
    this.subscriptions.push(emitValueSubscription)
  }

 ngOnDestroy(): void {
  this.subscriptions.forEach(sub => sub.unsubscribe());
 }
}
